import type { ComponentChildren } from 'preact';
import { useLocation } from 'preact-iso';
import { useEffect, useState } from 'preact/hooks';
import { Layout } from '../../components/Layout';
import { useMe, challengeUrl } from '../../auth/useMe';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut';
import { PageContainer } from '../PageContainer';
import { SectionHeading } from '../SectionHeading';
import { TwoColumnShell } from '../TwoColumnShell';
import { AccountNavRail } from './AccountNavRail';

/**
 * Universal /me/account chrome per ADR-0015 + legacy ADR-0030. Wraps
 * the public site Layout (top nav + footnote) and renders the
 * AccountNavRail down the left of a two-column shell. Anonymous users
 * are bounced to the sign-in challenge with a ?return= back to the
 * page they hit.
 *
 * <p>Mirrors AdminShell's structure (eyebrow + serif title + subtitle
 * + two-column body + mobile drawer) so the navigation muscle memory
 * carries over from the curator console.</p>
 */
export function AccountShell({
    title,
    subtitle,
    eyebrow = 'Account',
    children,
}: {
    title: string;
    subtitle?: ComponentChildren;
    eyebrow?: string;
    children: ComponentChildren;
}) {
    const location = useLocation();
    const currentPath = location.path || '/me/account';
    useDocumentTitle(`${title} - Gposingway`);

    const me = useMe();

    const [drawerOpen, setDrawerOpen] = useState(false);
    useKeyboardShortcut(
        'Escape',
        () => drawerOpen && setDrawerOpen(false),
        [drawerOpen],
    );
    useEffect(() => {
        setDrawerOpen(false);
    }, [currentPath]);

    // Anonymous → bounce to sign-in. Keep the loading state inert so we
    // don't flash content before the redirect.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (me.status === 'anonymous') {
            window.location.replace(challengeUrl('discord', currentPath));
        }
    }, [me.status, currentPath]);

    if (me.status === 'loading' || me.status === 'anonymous') {
        return (
            <Layout>
                <PageContainer>
                    <p class="py-12 text-center text-sm text-fg-muted">
                        {me.status === 'loading'
                            ? 'Resolving your account…'
                            : 'Sending you to sign-in…'}
                    </p>
                </PageContainer>
            </Layout>
        );
    }

    const user = me.status === 'authenticated' ? me.user : undefined;
    const slug = user?.claims?.slug?.[0] ?? null;
    const displayLabel = user?.displayName ?? user?.id ?? null;

    return (
        <Layout>
            <header class="border-b border-rule bg-surface-base/40">
                <div class="mx-auto w-full max-w-[1680px] px-[clamp(20px,3.5vw,56px)] py-8">
                    <div class="flex items-start justify-between gap-4">
                        <SectionHeading
                            eyebrow={eyebrow}
                            title={title}
                            subtitle={subtitle}
                            size="md"
                        />
                        <button
                            type="button"
                            onClick={() => setDrawerOpen(true)}
                            aria-label="Open account navigation"
                            class="inline-flex h-9 w-9 items-center justify-center rounded border border-rule text-fg-primary transition-colors hover:border-accent hover:text-accent lg:hidden"
                        >
                            <span aria-hidden="true" class="text-lg leading-none">
                                ≡
                            </span>
                        </button>
                    </div>
                </div>
            </header>

            <main class="flex-1">
                <TwoColumnShell
                    sidebar={
                        <AccountNavRail
                            currentPath={currentPath}
                            displayLabel={displayLabel}
                            slug={slug}
                        />
                    }
                >
                    {children}
                </TwoColumnShell>
            </main>

            {drawerOpen && (
                <div class="fixed inset-0 z-50 flex lg:hidden">
                    <button
                        type="button"
                        aria-label="Close navigation"
                        onClick={() => setDrawerOpen(false)}
                        class="flex-1 cursor-default bg-surface-base/70 backdrop-blur-sm"
                    />
                    <div class="h-full w-72 max-w-[80vw] border-l border-rule bg-surface-raised shadow-2xl">
                        <div class="flex items-center justify-between border-b border-rule px-4 py-3">
                            <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                                Account
                            </span>
                            <button
                                type="button"
                                onClick={() => setDrawerOpen(false)}
                                aria-label="Close"
                                class="flex h-7 w-7 items-center justify-center rounded text-fg-muted transition-colors hover:bg-accent-glow hover:text-fg-primary"
                            >
                                <span aria-hidden="true" class="text-lg leading-none">
                                    ×
                                </span>
                            </button>
                        </div>
                        <AccountNavRail
                            currentPath={currentPath}
                            displayLabel={displayLabel}
                            slug={slug}
                            onNavigate={() => setDrawerOpen(false)}
                        />
                    </div>
                </div>
            )}
        </Layout>
    );
}
